import React, { useState, useEffect } from "react";
import { 
  Send, 
  Plus, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  Trash2,
  Pencil,
  Loader2,
  X,
  CalendarDays,
  RefreshCw,
  FileDown
} from "lucide-react";
import { Layout } from "../components/Layout";
import { RelatorioFilterModal } from "../components/RelatorioFilterModal";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { apiRequest, formatDate } from "../lib/utils";
import { toast } from "../components/ToastProvider";
import { AdminPasswordDialog } from "../components/AdminPasswordDialog";
import { ConfirmacaoRegistroDialog } from "../components/ConfirmacaoRegistroDialog";
import { MarcarEnvioDialog } from "../components/MarcarEnvioDialog";

// Opções de recorrência
const RECORRENCIAS = [
  { value: "mensal", label: "Mensal", meses: 1 },
  { value: "bimestral", label: "Bimestral", meses: 2 },
  { value: "trimestral", label: "Trimestral", meses: 3 },
  { value: "semestral", label: "Semestral", meses: 6 },
  { value: "anual", label: "Anual", meses: 12 },
];

// Dias do mês para envio
const DIAS_MES = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: `Dia ${i + 1}`,
}));

export const Envios = () => {
  const [envios, setEnvios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEnvio, setEditingEnvio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  
  // Estado para diálogo de confirmação de registro
  const [showConfirmacaoDialog, setShowConfirmacaoDialog] = useState(false);
  const [envioRecemCriado, setEnvioRecemCriado] = useState(null);
  
  // Estado para diálogo de marcar envio com evidência
  const [showMarcarEnvioDialog, setShowMarcarEnvioDialog] = useState(false);
  const [envioParaMarcar, setEnvioParaMarcar] = useState(null);
  const [relatorioModalOpen, setRelatorioModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    recorrencia: "mensal",
    dia_envio: "1",
    dias_antecedencia_alerta: "5",
  });

  useEffect(() => {
    fetchEnvios();
  }, []);

  const fetchEnvios = async () => {
    try {
      setLoading(true);
      const data = await apiRequest("/envios");
      setEnvios(data);
    } catch (error) {
      toast.error("Erro ao carregar envios");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (envio = null) => {
    if (envio) {
      setEditingEnvio(envio);
      setFormData({
        nome: envio.nome,
        descricao: envio.descricao || "",
        recorrencia: envio.recorrencia || "mensal",
        dia_envio: String(envio.dia_envio),
        dias_antecedencia_alerta: String(envio.dias_antecedencia_alerta || 5),
      });
    } else {
      setEditingEnvio(null);
      setFormData({
        nome: "",
        descricao: "",
        recorrencia: "mensal",
        dia_envio: "1",
        dias_antecedencia_alerta: "5",
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.dia_envio) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        dia_envio: parseInt(formData.dia_envio),
        dias_antecedencia_alerta: parseInt(formData.dias_antecedencia_alerta),
      };

      if (editingEnvio) {
        await apiRequest(`/envios/${editingEnvio.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.success("Envio atualizado com sucesso!");
        setDialogOpen(false);
        fetchEnvios();
      } else {
        // Criar o envio
        const novoEnvio = await apiRequest("/envios", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        
        // Verificar se o dia já passou neste mês
        const diaAtual = new Date().getDate();
        const diaEnvio = parseInt(formData.dia_envio);
        
        if (diaAtual > diaEnvio) {
          // Dia já passou - mostrar diálogo de confirmação
          setEnvioRecemCriado({
            ...novoEnvio,
            nome: formData.nome,
            dia_envio: diaEnvio,
            recorrencia: formData.recorrencia,
          });
          setDialogOpen(false);
          setShowConfirmacaoDialog(true);
          toast.success("Envio cadastrado!");
        } else {
          toast.success("Envio cadastrado com sucesso!");
          setDialogOpen(false);
        }
        fetchEnvios();
      }
    } catch (error) {
      toast.error(error.message || "Erro ao salvar envio");
    } finally {
      setSaving(false);
    }
  };

  // Marcar envio recém-criado como já realizado
  const handleConfirmarEnvioRealizado = async () => {
    if (!envioRecemCriado) return;
    
    try {
      await apiRequest(`/envios/${envioRecemCriado.id}/marcar-enviado`, {
        method: "POST",
      });
      toast.success("Envio marcado como realizado!");
      fetchEnvios();
    } catch (error) {
      toast.error(error.message || "Erro ao marcar envio");
    }
  };

  // Ignorar período atual (já está tratado pelo backend)
  const handleIgnorarPeriodoAtual = async () => {
    // Não precisa fazer nada - o backend já ignora envios criados após o dia
    toast.info("O sistema começará a alertar no próximo período.");
  };

  // Abrir diálogo para marcar envio com observações e evidência
  const handleAbrirMarcarEnvio = (envio) => {
    setEnvioParaMarcar(envio);
    setShowMarcarEnvioDialog(true);
  };

  const handleEnvioMarcadoSuccess = () => {
    fetchEnvios();
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowPasswordDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiRequest(`/envios/${deleteId}`, { method: "DELETE" });
      toast.success("Envio excluído com sucesso!");
      setDeleteId(null);
      fetchEnvios();
    } catch (error) {
      toast.error(error.message || "Erro ao excluir envio");
    }
  };

  const handleMarcarEnviado = async (envioId) => {
    try {
      await apiRequest(`/envios/${envioId}/marcar-enviado`, { method: "POST" });
      toast.success("Envio marcado como realizado!");
      fetchEnvios();
    } catch (error) {
      toast.error(error.message || "Erro ao marcar envio");
    }
  };

  const getRecorrenciaLabel = (recorrencia) => {
    const found = RECORRENCIAS.find(r => r.value === recorrencia);
    return found ? found.label : recorrencia;
  };

  const getStatusEnvio = (envio) => {
    const hoje = new Date();
    const diaAtual = hoje.getDate();
    const mesAtual = hoje.getMonth();
    const anoAtual = hoje.getFullYear();
    
    // Obter os meses da recorrência
    const recorrenciaInfo = RECORRENCIAS.find(r => r.value === (envio.recorrencia || "mensal"));
    const mesesRecorrencia = recorrenciaInfo ? recorrenciaInfo.meses : 1;
    
    // Verificar se já foi enviado no período atual da recorrência
    if (envio.ultimo_envio) {
      const ultimoEnvio = new Date(envio.ultimo_envio);
      
      // Calcular o próximo mês de envio baseado no último envio
      const proximoEnvio = new Date(ultimoEnvio);
      proximoEnvio.setMonth(proximoEnvio.getMonth() + mesesRecorrencia);
      
      // Se ainda não chegou o próximo período, está em dia
      if (hoje < proximoEnvio) {
        return { status: "enviado", label: "Em dia", color: "bg-green-100 text-green-800" };
      }
    }
    
    // Verificar se está no mês correto para envio (baseado na recorrência)
    // Para simplificar, vamos considerar que todo mês tem chance de ser mês de envio
    // A lógica real seria baseada no mês inicial + recorrência
    
    // Verificar se está atrasado (já passou o dia do mês atual)
    if (diaAtual > envio.dia_envio) {
      return { status: "atrasado", label: "Atrasado", color: "bg-red-100 text-red-800" };
    }
    
    // Verificar se está no período de alerta
    const diasParaEnvio = envio.dia_envio - diaAtual;
    if (diasParaEnvio <= envio.dias_antecedencia_alerta) {
      return { status: "pendente", label: `Faltam ${diasParaEnvio} dias`, color: "bg-amber-100 text-amber-800" };
    }
    
    return { status: "aguardando", label: `Dia ${envio.dia_envio}`, color: "bg-slate-100 text-slate-800" };
  };

  // Estatísticas
  const estatisticas = {
    total: envios.length,
    enviados: envios.filter(e => getStatusEnvio(e).status === "enviado").length,
    pendentes: envios.filter(e => getStatusEnvio(e).status === "pendente").length,
    atrasados: envios.filter(e => getStatusEnvio(e).status === "atrasado").length,
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Envios
            </h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Aqui serão cadastrados envios que devem ser feitos mensalmente, como por exemplo material para análise, Siproquim, SNGPC e outras demandas.</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              onClick={() => setRelatorioModalOpen(true)}
              data-testid="relatorio-envios-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Relatório PDF
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-teal-600 hover:bg-teal-700 flex-1 sm:flex-none">
              <Plus className="w-4 h-4 mr-2" />
              Novo Envio
            </Button>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-center sm:text-left">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Send className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{estatisticas.total}</p>
                  <p className="text-xs sm:text-sm text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-center sm:text-left">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-green-700">{estatisticas.enviados}</p>
                  <p className="text-xs sm:text-sm text-green-600">Enviados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-center sm:text-left">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-amber-700">{estatisticas.pendentes}</p>
                  <p className="text-xs sm:text-sm text-amber-600">Pendentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-center sm:text-left">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-red-700">{estatisticas.atrasados}</p>
                  <p className="text-xs sm:text-sm text-red-600">Atrasados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabela de Envios */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Envios Cadastrados</CardTitle>
                <CardDescription>Lista de todos os envios mensais obrigatórios</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchEnvios}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
              </div>
            ) : envios.length === 0 ? (
              <div className="text-center py-8">
                <Send className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nenhum envio cadastrado</p>
                <p className="text-sm text-slate-400 mt-1">Clique em "Novo Envio" para começar</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead className="hidden sm:table-cell">Recorrência</TableHead>
                      <TableHead className="hidden md:table-cell">Dia</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Último Envio</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {envios.map((envio) => {
                      const statusInfo = getStatusEnvio(envio);
                      return (
                        <TableRow key={envio.id}>
                          <TableCell className="font-medium">
                            {envio.nome}
                            <span className="block sm:hidden text-xs text-slate-500 font-normal">
                              {getRecorrenciaLabel(envio.recorrencia || "mensal")} • Dia {envio.dia_envio}
                            </span>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Badge variant="outline" className="font-normal">
                              {getRecorrenciaLabel(envio.recorrencia || "mensal")}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div className="flex items-center gap-2">
                              <CalendarDays className="w-4 h-4 text-slate-400" />
                              {envio.dia_envio}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                            {envio.ultimo_envio ? formatDate(envio.ultimo_envio) : "Nunca"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1 sm:gap-2">
                              {statusInfo.status !== "enviado" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAbrirMarcarEnvio(envio)}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50 px-2 sm:px-3"
                                >
                                  <CheckCircle2 className="w-4 h-4 sm:mr-1" />
                                  <span className="hidden sm:inline">Enviado</span>
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenDialog(envio)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClick(envio.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal de Cadastro/Edição */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                {editingEnvio ? "Editar Envio" : "Novo Envio Mensal"}
              </DialogTitle>
              <DialogDescription>
                {editingEnvio 
                  ? "Atualize as informações do envio mensal" 
                  : "Cadastre um novo envio obrigatório mensal"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Envio *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: SNGPC, Análise de Água..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="recorrencia">Recorrência *</Label>
                <Select
                  value={formData.recorrencia}
                  onValueChange={(value) => setFormData({ ...formData, recorrencia: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a recorrência" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECORRENCIAS.map((rec) => (
                      <SelectItem key={rec.value} value={rec.value}>
                        {rec.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {formData.recorrencia === "mensal" && "O envio deve ser feito todo mês"}
                  {formData.recorrencia === "bimestral" && "O envio deve ser feito a cada 2 meses"}
                  {formData.recorrencia === "trimestral" && "O envio deve ser feito a cada 3 meses"}
                  {formData.recorrencia === "semestral" && "O envio deve ser feito a cada 6 meses"}
                  {formData.recorrencia === "anual" && "O envio deve ser feito uma vez por ano"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dia_envio">Dia do Mês para Envio *</Label>
                  <Select
                    value={formData.dia_envio}
                    onValueChange={(value) => setFormData({ ...formData, dia_envio: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Dia" />
                    </SelectTrigger>
                    <SelectContent>
                      {DIAS_MES.map((dia) => (
                        <SelectItem key={dia.value} value={dia.value}>
                          {dia.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dias_antecedencia">Dias de Antecedência para Alerta</Label>
                  <Select
                    value={formData.dias_antecedencia_alerta}
                    onValueChange={(value) => setFormData({ ...formData, dias_antecedencia_alerta: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Dias" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 5, 7, 10, 15].map((dias) => (
                        <SelectItem key={dias} value={String(dias)}>
                          {dias} {dias === 1 ? "dia" : "dias"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição (opcional)</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Informações adicionais sobre este envio..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  editingEnvio ? "Atualizar" : "Cadastrar"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmação de exclusão com senha */}
        <AdminPasswordDialog
          open={showPasswordDialog}
          onOpenChange={setShowPasswordDialog}
          onConfirm={handleDeleteConfirm}
          title="Excluir Envio"
          description="Esta ação não pode ser desfeita. O envio será removido permanentemente."
        />

        {/* Dialog de confirmação de registro */}
        <ConfirmacaoRegistroDialog
          open={showConfirmacaoDialog}
          onOpenChange={setShowConfirmacaoDialog}
          onConfirmRegistrar={handleConfirmarEnvioRealizado}
          onConfirmIgnorar={handleIgnorarPeriodoAtual}
          tipo="envio"
          nomeItem={envioRecemCriado?.nome || ""}
          detalhes={envioRecemCriado ? `Dia ${envioRecemCriado.dia_envio} - ${envioRecemCriado.recorrencia?.charAt(0).toUpperCase() + envioRecemCriado.recorrencia?.slice(1)}` : ""}
        />

        {/* Dialog de marcar envio com evidência */}
        <MarcarEnvioDialog
          open={showMarcarEnvioDialog}
          onOpenChange={setShowMarcarEnvioDialog}
          envio={envioParaMarcar}
          onSuccess={handleEnvioMarcadoSuccess}
        />

        {/* Modal de Relatório PDF */}
        <RelatorioFilterModal
          open={relatorioModalOpen}
          onOpenChange={setRelatorioModalOpen}
          title="Relatório de Envios"
          description="Selecione os filtros para gerar o relatório em PDF"
          endpoint="/pdf/envios"
          filters={[
            {
              key: "recorrencia",
              label: "Recorrência",
              type: "select",
              options: RECORRENCIAS
            },
            {
              key: "status",
              label: "Status",
              type: "select",
              options: [
                { value: "em_dia", label: "Em dia" },
                { value: "pendente", label: "Pendente" },
                { value: "atrasado", label: "Atrasado" },
                { value: "aguardando", label: "Aguardando" },
              ]
            },
            {
              key: "data_inicio",
              label: "Último envio a partir de",
              type: "date"
            },
            {
              key: "data_fim",
              label: "Último envio até",
              type: "date"
            }
          ]}
        />
      </div>
    </Layout>
  );
};

export default Envios;
